import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, User, Mail, Phone, MapPin, Receipt, Calendar, Heart, BadgePercent, ShieldCheck, Edit2, Save, X, Loader2, AlertCircle, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function AdminClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/admin/clients/${id}`).then((r) => {
      setData(r.data);
      setForm({
        prenom: r.data.client.prenom || "",
        nom: r.data.client.nom || "",
        telephone: r.data.client.telephone || "",
        sexe: r.data.client.sexe || "",
        date_naissance: r.data.client.date_naissance || "",
        adresse: r.data.client.adresse || "",
        code_postal: r.data.client.code_postal || "",
        ville: r.data.client.ville || "",
        fiscal_email: r.data.client.fiscal_email || "",
        urssaf_consent: !!r.data.client.urssaf_consent,
        notes: r.data.client.notes || "",
      });
    }).catch(() => toast.error("Client introuvable"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/clients/${id}`, form);
      toast.success("Fiche mise à jour");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="p-12 text-center text-fegray"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data) return <div className="p-12 text-center text-fegray">Client introuvable</div>;

  const { client, stats, reservations } = data;
  const fullName = `${client.prenom || ""} ${client.nom || ""}`.trim();
  const initials = ((client.prenom?.[0] || "") + (client.nom?.[0] || "")).toUpperCase();
  const sexeLabel = { F: "Femme", M: "Homme", NB: "Non-binaire" }[client.sexe] || "—";
  const age = client.date_naissance
    ? Math.floor((new Date() - new Date(client.date_naissance)) / (365.25 * 24 * 3600 * 1000))
    : null;

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <Link to="/admin/clients" data-testid="back-to-clients" className="inline-flex items-center gap-2 text-sm text-fegray hover:text-fegreen font-semibold mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour à la liste
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* Identity card */}
        <div className="lg:col-span-1 fe-card p-7">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-4">
              <span className="w-16 h-16 rounded-full bg-fegreen text-white text-xl font-bold flex items-center justify-center shrink-0">
                {initials || "?"}
              </span>
              <div>
                <h1 className="text-xl font-bold text-fegray-dark leading-tight">{fullName || "Sans nom"}</h1>
                <p className="text-xs text-fegray mt-0.5">Client depuis {client.created_at ? new Date(client.created_at).toLocaleDateString("fr-FR") : "—"}</p>
              </div>
            </div>
            {!editing && (
              <button onClick={() => setEditing(true)} className="text-fegreen hover:text-fegreen/80" data-testid="edit-client-btn" aria-label="Modifier">
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {!editing ? (
            <div className="space-y-3 text-sm">
              <Row icon={Mail} label="Email" value={client.email} />
              <Row icon={Phone} label="Téléphone" value={client.telephone} link={`tel:${client.telephone}`} />
              <Row icon={User} label="Sexe" value={sexeLabel + (age !== null ? ` · ${age} ans` : "")} />
              {client.adresse && (
                <Row icon={MapPin} label="Adresse" value={`${client.adresse}, ${client.code_postal || ""} ${client.ville || ""}`} />
              )}
              <div className="pt-3 mt-3 border-t border-gray-100">
                <p className="text-xs text-fegray mb-2">Crédit d'impôt</p>
                <p className="text-xs text-fegray-dark">
                  {client.fiscal_email ? <>Email fiscal : <strong>{client.fiscal_email}</strong></> : "Aucun email fiscal renseigné"}
                </p>
                <p className="text-xs mt-1">
                  {client.urssaf_consent ? <span className="text-fegreen font-semibold">✓ Autorisation URSSAF</span> : <span className="text-amber-600">URSSAF non autorisée</span>}
                </p>
              </div>
              {client.notes && (
                <div className="pt-3 mt-3 border-t border-gray-100">
                  <p className="text-xs text-fegray mb-1">Notes admin</p>
                  <p className="text-xs text-fegray-dark italic">{client.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={save} className="space-y-2.5">
              <Mini label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} />
              <Mini label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} />
              <Mini label="Téléphone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} />
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-fegray mb-1">Sexe</label>
                <select value={form.sexe} onChange={(e) => setForm({ ...form, sexe: e.target.value })} className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-fegreen focus:outline-none">
                  <option value="">— Non renseigné —</option>
                  <option value="F">Femme</option>
                  <option value="M">Homme</option>
                  <option value="NB">Non-binaire</option>
                </select>
              </div>
              <Mini label="Date de naissance" type="date" value={form.date_naissance} onChange={(v) => setForm({ ...form, date_naissance: v })} />
              <Mini label="Adresse" value={form.adresse} onChange={(v) => setForm({ ...form, adresse: v })} />
              <div className="grid grid-cols-3 gap-2">
                <Mini label="CP" value={form.code_postal} onChange={(v) => setForm({ ...form, code_postal: v })} />
                <div className="col-span-2">
                  <Mini label="Ville" value={form.ville} onChange={(v) => setForm({ ...form, ville: v })} />
                </div>
              </div>
              <Mini label="Email fiscal" type="email" value={form.fiscal_email} onChange={(v) => setForm({ ...form, fiscal_email: v })} />
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-fegray mb-1">Notes admin (privé)</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-fegreen focus:outline-none resize-none" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" data-testid="save-client-btn" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-fegreen text-white text-sm font-bold py-2 rounded-full hover:bg-fegreen/90">
                  <Save className="w-3.5 h-3.5" /> Enregistrer
                </button>
                <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 rounded-full border border-gray-200 text-fegray-dark text-sm font-semibold hover:bg-gray-50">
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Stats */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
          <Stat icon={Calendar} label="Réservations" value={stats.nb_reservations} sub={`${stats.nb_paid} payées`} />
          <Stat icon={Receipt} label="Total dépensé" value={`${stats.total_spent.toFixed(2)}€`} accent />
          <Stat icon={BadgePercent} label="Panier moyen" value={`${stats.avg_basket.toFixed(2)}€`} />
          <Stat icon={Repeat} label="Fréquence des séances" value={stats.frequency_label || "—"} sub={stats.frequency_days ? `≈ tous les ${stats.frequency_days} jours` : null} compact />
          <Stat icon={Heart} label="Pack favori" value={stats.favorite_pack || "—"} compact />
          <Stat icon={ShieldCheck} label="Crédit d'impôt cumulé" value={`${stats.credit_saved.toFixed(2)}€`} />
          <Stat icon={User} label="Séances suivies" value={stats.sessions_total} />
        </div>
      </div>

      {/* Reservations */}
      <div className="fe-card p-7">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-fegray-dark">Historique des séances</h2>
          <span className="text-xs text-fegray">{reservations.length} entrée(s)</span>
        </div>
        {reservations.length === 0 ? (
          <p className="text-sm text-fegray text-center py-8">Aucune réservation pour ce client.</p>
        ) : (
          <div className="space-y-3">
            {reservations.map((r) => (
              <div key={r.id} className="border border-gray-100 rounded-xl p-4" data-testid={`client-res-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-bold text-fegray-dark text-sm">{r.pack_name}</div>
                    {r.addon_pack_name && (
                      <div className="text-xs text-fegreen mt-0.5">+ {r.addon_pack_name}</div>
                    )}
                    <div className="text-xs text-fegray mt-1">
                      {(r.slots || []).length || 1} séance(s) · Première : {r.date_souhaitee} à {r.horaire}
                    </div>
                    <div className="text-[10px] text-fegray mt-1">
                      Créée le {r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <ResStatusBadge status={r.payment_status} />
                    <div className="text-sm font-bold text-fegreen mt-1">
                      {(r.amount_after_credit || r.amount || 0).toFixed(2)}€
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, link }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-fegreen mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-fegray font-bold">{label}</p>
        {link ? (
          <a href={link} className="text-fegray-dark text-sm hover:text-fegreen break-all">{value || "—"}</a>
        ) : (
          <p className="text-fegray-dark text-sm break-words">{value || "—"}</p>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-fegray mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-fegray-dark focus:border-fegreen focus:outline-none"
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, compact }) {
  return (
    <div
      className={`rounded-2xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.06)] ${
        accent ? "bg-fegreen" : "bg-white"
      }`}
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <Icon className={`w-5 h-5 mb-2 ${accent ? "text-white" : "text-fegreen"}`} strokeWidth={1.8} />
      <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${accent ? "text-white/80" : "text-fegray"}`}>{label}</div>
      <div className={`${compact ? "text-sm leading-snug" : "text-2xl"} font-extrabold ${accent ? "text-white" : "text-fegray-dark"}`}>{value}</div>
      {sub && <div className={`text-[10px] mt-0.5 ${accent ? "text-white/80" : "text-fegray"}`}>{sub}</div>}
    </div>
  );
}

function ResStatusBadge({ status }) {
  const map = {
    paid: { label: "Payée", cls: "bg-fegreen text-white" },
    pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
    unpaid: { label: "Non payée", cls: "bg-gray-100 text-fegray-dark" },
    cancelled: { label: "Annulée", cls: "bg-red-100 text-red-600" },
  };
  const cfg = map[status] || { label: status || "—", cls: "bg-gray-100 text-fegray-dark" };
  return <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${cfg.cls}`}>{cfg.label}</span>;
}
